// ============================================================
// JuridictionView.jsx — Vue "Par juridiction" pour Valoris
// Vue séparée, accessible depuis la nav, sélecteur cédant/acquéreur
// ============================================================
// INTÉGRATION dans sov_v8.jsx :
//
// 1. Importer ce fichier :
//    import JuridictionView from "./JuridictionView";
//
// 2. Dans le <nav>, ajouter un bouton après le compteur de modèles :
//    <button onClick={()=>setView("jurisdictions")} style={...}>
//      🌍 {lang==="fr" ? "Par juridiction" : "By jurisdiction"}
//    </button>
//
// 3. Dans le bloc de routing (après les deux lignes view==="dash" et view==="rapport") :
//    {view==="jurisdictions" && (
//      <JuridictionView
//        modelResults={modelResults}
//        L={L}
//        lang={lang}
//        onBack={()=>setView("dash")}
//      />
//    )}
// ============================================================

import { useState } from "react";

// ── Données référentielles par pays ──────────────────────────
const COUNTRIES = [
  {
    id: "fr",
    flag: "🇫🇷",
    label: "France",
    color: "#1a56e8",
    accent: "rgba(26,86,232,0.12)",
    ref: "Guide DGFiP — Nov. 2006",
    refShort: "DGFiP 2006",
    methods: [
      {
        key: "comb",
        name: "Combinaison DGFiP",
        type: "Pondération réglementaire",
        ref: "DGFiP §Comb.",
        primary: true,
        desc: "VM + V.Rent. pondérée selon la taille et la nature du paquet de titres. Méthode prescrite pour donations et successions.",
        getValue: (mr) => mr.comb?.equity_value ?? null,
        getParams: (mr) => {
          const r = mr.comb; if (!r) return [];
          return [
            { label: "Valeur math. (VM)", value: r.vm },
            { label: "Valeur de rend. (V.Rent)", value: r.vrent },
            { label: "Pondération VM", value: r.ponderation?.vm ?? 1, unit: "×" },
            { label: "Pondération V.Rent", value: r.ponderation?.vrent ?? 2, unit: "×" },
            { label: "Décote minorité", value: r.decote_minorite, pct: true },
          ];
        },
      },
      {
        key: "vp",
        name: "Valeur de Productivité",
        type: "Capitalisation du bénéfice",
        ref: "DGFiP §VP",
        primary: false,
        desc: "Capitalise le bénéfice net moyen pondéré au taux sans risque déflaté + β×5%.",
        getValue: (mr) => mr.vp?.equity_value ?? mr.comb?.detail?.vp?.equity_value ?? null,
        getParams: (mr) => {
          const r = mr.vp ?? mr.comb?.detail?.vp; if (!r) return [];
          return [
            { label: "Bénéfice normalisé", value: r.benefice_normalise },
            { label: "Taux capitalisation", value: r.taux_capitalisation, pct: true },
            { label: "Prime de risque", value: r.prime_risque, pct: true },
          ];
        },
      },
      {
        key: "vm",
        name: "Valeur Mathématique",
        type: "Actif net réévalué + goodwill",
        ref: "DGFiP §VM",
        primary: false,
        desc: "Réévalue les capitaux propres aux valeurs vénales et ajoute optionnellement la survaleur.",
        getValue: (mr) => mr.vm?.equity_value ?? mr.comb?.detail?.vm?.equity_value ?? null,
        getParams: (mr) => {
          const r = mr.vm ?? mr.comb?.detail?.vm; if (!r) return [];
          return [
            { label: "Capitaux propres", value: r.capitaux_propres },
            { label: "Plus-values latentes", value: r.total_plus_values },
            { label: "Survaleur (goodwill)", value: r.survaleur },
          ];
        },
      },
      {
        key: "mba",
        name: "MBA (Marge Brute d'Autofinancement)",
        type: "Multiple de capacité d'autofinancement",
        ref: "DGFiP §MBA",
        primary: false,
        desc: "Multiple de la MBA (résultat net + dotations). Adapté aux sociétés industrielles.",
        getValue: (mr) => mr.mba?.equity_value ?? null,
        getParams: (mr) => {
          const r = mr.mba; if (!r) return [];
          return [
            { label: "MBA", value: r.mba },
            { label: "Coefficient", value: r.coefficient, unit: "×" },
            { label: "Endettement net", value: r.endettement_net },
          ];
        },
      },
      {
        key: "ebe_re",
        name: "EBE / Résultat d'Exploitation",
        type: "Multiple EBITDA / EBIT",
        ref: "DGFiP §EBE/RE",
        primary: false,
        desc: "Applique un multiple sectoriel à l'EBE ou au RE puis déduit l'endettement net.",
        getValue: (mr) => mr.ebe_re?.equity_value ?? null,
        getParams: (mr) => {
          const r = mr.ebe_re; if (!r) return [];
          return [
            { label: "EBE / RE", value: r.ebe ?? r.re },
            { label: "Multiple", value: r.multiple, unit: "×" },
            { label: "EV résultante", value: r.valeur_enterprise },
          ];
        },
      },
    ],
  },
  {
    id: "us",
    flag: "🇺🇸",
    label: "États-Unis",
    color: "#b91c1c",
    accent: "rgba(185,28,28,0.10)",
    ref: "Rev. Ruling 59-60 + IRM 4.48.4 (IRS)",
    refShort: "IRS 59-60",
    methods: [
      {
        key: "dcf",
        name: "DCF — Income Approach",
        type: "Discounted Cash Flow",
        ref: "Rev. Ruling 59-60 §4",
        primary: true,
        desc: "Méthode de référence IRS. Actualise les FCFF au WACC. Valeur = Fair Market Value (willing buyer / seller).",
        getValue: (mr) => mr.dcf?.equity_value ?? null,
        getParams: (mr) => {
          const r = mr.dcf; const p = r?._params ?? {}; if (!r) return [];
          return [
            { label: "WACC initial", value: p.cost_of_capital_initial, pct: true },
            { label: "Croissance terminale", value: p.terminal_growth_rate, pct: true },
            { label: "% Valeur terminale / EV", value: r.terminal_value_pct, pct: true },
            { label: "Années projetées", value: p.forecast_years, unit: " ans" },
          ];
        },
      },
      {
        key: "exc",
        name: "Excess Return (ROIC-WACC)",
        type: "Economic Profit Model",
        ref: "IRM 4.48.5",
        primary: false,
        desc: "Valorise l'entreprise comme capitaux investis + VA des profits économiques futurs. Cohérent avec IRM 4.48.5.",
        getValue: (mr) => mr.exc?.equity_value ?? null,
        getParams: (mr) => {
          const r = mr.exc; const p = r?._params ?? {}; if (!r) return [];
          return [
            { label: "ROIC initial", value: p.roic_initial, pct: true },
            { label: "WACC", value: p.cost_of_capital, pct: true },
            { label: "Spread ROIC-WACC", value: r.avg_roic_wacc_spread, pct: true },
            { label: "Valeur créée", value: r.total_value_created },
          ];
        },
      },
      {
        key: "mul",
        name: "Market Multiples",
        type: "Comparable Companies",
        ref: "Rev. Ruling 59-60 §5",
        primary: false,
        desc: "Comparaison aux sociétés cotées du secteur. Facteur 7 des 8 critères de la Rev. Ruling 59-60.",
        getValue: (mr) => mr.mul?.equity_value_mid ?? null,
        getParams: (mr) => {
          const r = mr.mul; if (!r) return [];
          return [
            { label: "Equity Low", value: r.equity_value_low },
            { label: "Equity Mid", value: r.equity_value_mid },
            { label: "Equity High", value: r.equity_value_high },
          ];
        },
      },
    ],
  },
  {
    id: "de",
    flag: "🇩🇪",
    label: "Allemagne",
    color: "#b45309",
    accent: "rgba(180,83,9,0.10)",
    ref: "IDW S1 (2008) + IDW S5",
    refShort: "IDW S1/S5",
    methods: [
      {
        key: "exc",
        name: "Ertragswertverfahren",
        type: "Capitalisation des revenus futurs",
        ref: "IDW S1 §§164-167",
        primary: true,
        desc: "Méthode dominante IDW S1. Valeur objectivée vérifiable par des tiers. Basiszinssatz Bundesbank + CAPM.",
        getValue: (mr) => mr.exc?.equity_value ?? mr.dcf?.equity_value ?? null,
        getParams: (mr) => {
          const r = mr.exc ?? mr.dcf; const p = r?._params ?? {}; if (!r) return [];
          return [
            { label: "Taux d'actualisation", value: p.cost_of_capital ?? p.cost_of_capital_initial, pct: true },
            { label: "Type de valeur", value: "Objectivée (IDW S1 §29)" },
            { label: "Base taux", value: "Basiszinssatz Bundesbank" },
            { label: "Complément", value: "IDW RS HFA 10 (comptes)" },
          ];
        },
      },
      {
        key: "dcf",
        name: "DCF (IDW S1 — variante)",
        type: "Discounted Cash Flow",
        ref: "IDW S1 §§100-120",
        primary: false,
        desc: "Variante DCF conforme IDW S1. Cohérent avec l'Ertragswertverfahren en régime permanent.",
        getValue: (mr) => mr.dcf?.equity_value ?? null,
        getParams: (mr) => {
          const r = mr.dcf; const p = r?._params ?? {}; if (!r) return [];
          return [
            { label: "WACC initial", value: p.cost_of_capital_initial, pct: true },
            { label: "WACC terminal", value: p.cost_of_capital_terminal, pct: true },
            { label: "Croissance terminale", value: p.terminal_growth_rate, pct: true },
          ];
        },
      },
      {
        key: "mul",
        name: "Multiples (vérification)",
        type: "Simplified pricing — IDW S1 §167",
        ref: "IDW S1 §167",
        primary: false,
        desc: "Utilisé uniquement comme contrôle de plausibilité du résultat principal (non méthode principale).",
        getValue: (mr) => mr.mul?.equity_value_mid ?? null,
        getParams: (mr) => {
          const r = mr.mul; if (!r) return [];
          return [
            { label: "Equity Mid (contrôle)", value: r.equity_value_mid },
            { label: "Usage IDW", value: "Plausibility check uniquement" },
          ];
        },
      },
    ],
  },
  {
    id: "ch",
    flag: "🇨🇭",
    label: "Suisse",
    color: "#be123c",
    accent: "rgba(190,18,60,0.10)",
    ref: "Circulaire CSI n°28 + AFC n°37",
    refShort: "CSI n°28",
    methods: [
      {
        key: "comb_ch",
        name: "Formule praticien CSI",
        type: "(VRend.×2 + VM×1) ÷ 3",
        ref: "CSI n°28 §3.1",
        primary: true,
        desc: "Formule canonique suisse : double de la valeur de rendement + valeur intrinsèque, divisé par 3. Applicable à toutes les administrations cantonales.",
        getValue: (mr) => {
          const vp = mr.vp?.equity_value ?? mr.comb?.detail?.vp?.equity_value;
          const vm = mr.vm?.equity_value ?? mr.comb?.detail?.vm?.equity_value;
          if (vp == null || vm == null) return null;
          return (vp * 2 + vm * 1) / 3;
        },
        getParams: (mr) => {
          const vp = mr.vp?.equity_value ?? mr.comb?.detail?.vp?.equity_value;
          const vm = mr.vm?.equity_value ?? mr.comb?.detail?.vm?.equity_value;
          if (vp == null || vm == null) return [];
          return [
            { label: "Valeur de rendement (VP)", value: vp },
            { label: "Valeur intrinsèque (VM)", value: vm },
            { label: "Pondération", value: "(VR×2 + VM×1) ÷ 3" },
            { label: "Décote minoritaire", value: "Si participation < 50%" },
          ];
        },
      },
      {
        key: "vm",
        name: "Valeur intrinsèque (VM)",
        type: "Actif net réévalué",
        ref: "CSI n°28 §2",
        primary: false,
        desc: "Composante patrimoniale de la formule CSI. Identique à la VM française.",
        getValue: (mr) => mr.vm?.equity_value ?? mr.comb?.detail?.vm?.equity_value ?? null,
        getParams: (mr) => {
          const r = mr.vm ?? mr.comb?.detail?.vm; if (!r) return [];
          return [
            { label: "Capitaux propres", value: r.capitaux_propres },
            { label: "Plus-values latentes", value: r.total_plus_values },
          ];
        },
      },
    ],
  },
  {
    id: "uk",
    flag: "🇬🇧",
    label: "Royaume-Uni",
    color: "#15803d",
    accent: "rgba(21,128,61,0.10)",
    ref: "HMRC SAV Manual (SVM + CG68300)",
    refShort: "HMRC SAV",
    methods: [
      {
        key: "dcf",
        name: "Open Market Value — Income",
        type: "Capitalisation / DCF",
        ref: "SVM107070 — TCGA 1992 §272",
        primary: true,
        desc: "HMRC valorise les titres non cotés à leur Open Market Value. Approche revenu (capitalisation des bénéfices ou DCF) + décote illiquidité standard.",
        getValue: (mr) => {
          const base = mr.dcf?.equity_value ?? mr.comb?.equity_value;
          return base != null ? base * (1 - 0.12) : null;
        },
        getParams: (mr) => {
          const base = mr.dcf?.equity_value ?? mr.comb?.equity_value;
          if (base == null) return [];
          return [
            { label: "Valeur base (Income)", value: base },
            { label: "Décote illiquidité HMRC", value: 0.12, pct: true },
            { label: "Open Market Value", value: base * (1 - 0.12) },
            { label: "Base fiscale", value: "CGT / Inheritance Tax" },
            { label: "Goodwill", value: "Traité par SAV (CG68300)" },
          ];
        },
      },
      {
        key: "mul",
        name: "Market Approach (P/E sectoriel)",
        type: "Capitalisation bénéfices / P/E",
        ref: "SVM107070",
        primary: false,
        desc: "HMRC utilise le P/E sectoriel ajusté comme méthode complémentaire pour les sociétés rentables.",
        getValue: (mr) => mr.mul?.equity_value_mid != null ? mr.mul.equity_value_mid * (1 - 0.12) : null,
        getParams: (mr) => {
          const r = mr.mul; if (!r) return [];
          return [
            { label: "Equity Mid (brut)", value: r.equity_value_mid },
            { label: "Décote illiquidité", value: 0.12, pct: true },
          ];
        },
      },
    ],
  },
];

// ── Helpers ──────────────────────────────────────────────────
const fC = (v) =>
  v == null || isNaN(v) || typeof v !== "number"
    ? typeof v === "string" ? v : "—"
    : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);

const fP = (v) =>
  v == null || isNaN(v) ? "—" : (v * 100).toFixed(2) + " %";

const fVal = (p) => {
  if (p.pct) return fP(p.value);
  if (p.unit) return (typeof p.value === "number" ? p.value : "—") + p.unit;
  return fC(p.value);
};

function Chip({ label, color }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, color,
      background: color + "20", border: `1px solid ${color}40`,
      padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

function MethodCard({ method, country, modelResults, isManual, manualValue, onManualChange }) {
  const [open, setOpen] = useState(false);
  const calcValue = method.getValue(modelResults);
  const displayValue = isManual ? manualValue : calcValue;
  const hasCalc = calcValue != null;
  const params = method.getParams(modelResults);

  return (
    <div style={{
      background: method.primary ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.02)",
      border: method.primary ? `1px solid ${country.color}50` : "1px solid rgba(255,255,255,0.06)",
      borderRadius: 8,
      marginBottom: 8,
      overflow: "hidden",
    }}>
      {/* Header */}
      <div
        onClick={() => setOpen(!open)}
        style={{ padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#f1f5f9" }}>{method.name}</span>
            {method.primary && <Chip label="Principale" color={country.color} />}
            {!hasCalc && <Chip label="Non calculé" color="#64748b" />}
          </div>
          <div style={{ fontSize: 10.5, color: "#64748b" }}>{method.type}</div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          {isManual ? (
            <input
              type="number"
              value={manualValue ?? ""}
              onChange={e => onManualChange(parseFloat(e.target.value) || null)}
              onClick={e => e.stopPropagation()}
              placeholder="Saisir..."
              style={{
                background: "rgba(255,255,255,0.06)", border: `1px solid ${country.color}60`,
                borderRadius: 5, padding: "3px 8px", color: "#f1f5f9",
                fontSize: 12, fontWeight: 700, width: 130, textAlign: "right",
              }}
            />
          ) : (
            <div style={{ fontSize: 14, fontWeight: 800, color: hasCalc ? "#f1f5f9" : "#334155" }}>
              {hasCalc ? fC(displayValue) : "—"}
            </div>
          )}
          <div style={{ fontSize: 9.5, color: country.color, marginTop: 2 }}>{method.ref}</div>
        </div>
      </div>

      {/* Drill-down */}
      {open && (
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)", padding: "8px 14px 10px" }}>
          <p style={{ fontSize: 11, color: "#64748b", margin: "0 0 8px", lineHeight: 1.5 }}>{method.desc}</p>
          {params.length > 0 && params.map((p, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: i < params.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
              <span style={{ fontSize: 11, color: "#64748b" }}>{p.label}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#cbd5e1" }}>{fVal(p)}</span>
            </div>
          ))}
          {params.length === 0 && (
            <div style={{ fontSize: 11, color: "#475569", fontStyle: "italic" }}>
              Lancez le calcul correspondant pour voir les paramètres détaillés.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CountryColumn({ countryId, modelResults, manualValues, onManualChange, onCountryChange }) {
  const country = COUNTRIES.find(c => c.id === countryId);
  const [manualMode, setManualMode] = useState(false);

  const primaryMethod = country.methods.find(m => m.primary);
  const primaryValue = manualMode
    ? (manualValues[countryId]?.[primaryMethod.key] ?? null)
    : primaryMethod.getValue(modelResults);

  return (
    <div style={{
      flex: 1, minWidth: 0,
      background: "rgba(255,255,255,0.02)",
      border: `1.5px solid ${country.color}40`,
      borderRadius: 12,
      overflow: "hidden",
    }}>
      {/* Country header */}
      <div style={{ background: country.accent, borderBottom: `1px solid ${country.color}30`, padding: "14px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 22 }}>{country.flag}</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#f1f5f9" }}>{country.label}</div>
              <div style={{ fontSize: 10, color: country.color, fontWeight: 600, marginTop: 1 }}>{country.refShort}</div>
            </div>
          </div>
          {/* Sélecteur pays */}
          <select
            value={countryId}
            onChange={e => onCountryChange(e.target.value)}
            style={{
              background: "rgba(0,0,0,0.3)", border: `1px solid ${country.color}50`,
              borderRadius: 6, color: "#94a3b8", fontSize: 11, padding: "3px 6px", cursor: "pointer",
            }}
          >
            {COUNTRIES.map(c => <option key={c.id} value={c.id}>{c.flag} {c.label}</option>)}
          </select>
        </div>

        {/* Valeur principale */}
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>
              Valeur principale — {primaryMethod.name}
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: primaryValue != null ? "#f1f5f9" : "#334155" }}>
              {primaryValue != null ? fC(primaryValue) : "Non calculé"}
            </div>
          </div>
          <button
            onClick={() => setManualMode(!manualMode)}
            style={{
              background: manualMode ? `${country.color}30` : "rgba(255,255,255,0.04)",
              border: `1px solid ${manualMode ? country.color : "rgba(255,255,255,0.1)"}`,
              borderRadius: 6, padding: "4px 10px", color: manualMode ? country.color : "#64748b",
              fontSize: 10, cursor: "pointer", fontWeight: 600,
            }}
          >
            {manualMode ? "✎ Manuel" : "✎ Saisir"}
          </button>
        </div>
      </div>

      {/* Référence guide */}
      <div style={{ padding: "8px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: 10.5, color: "#475569" }}>
        Réf. : <span style={{ color: "#64748b" }}>{country.ref}</span>
      </div>

      {/* Méthodes */}
      <div style={{ padding: "12px 16px" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
          Méthodes reconnues ({country.methods.length})
        </div>
        {country.methods.map(method => (
          <MethodCard
            key={method.key + country.id}
            method={method}
            country={country}
            modelResults={modelResults}
            isManual={manualMode}
            manualValue={manualValues[countryId]?.[method.key] ?? null}
            onManualChange={v => onManualChange(countryId, method.key, v)}
          />
        ))}
      </div>
    </div>
  );
}

// ── Composant principal ──────────────────────────────────────
export default function JuridictionView({ modelResults, L, lang, onBack }) {
  const [cedant, setCedant] = useState("fr");
  const [acquereur, setAcquereur] = useState("de");
  const [manualValues, setManualValues] = useState({});

  const handleManualChange = (countryId, methodKey, value) => {
    setManualValues(prev => ({
      ...prev,
      [countryId]: { ...(prev[countryId] ?? {}), [methodKey]: value },
    }));
  };

  // Calcul de l'écart entre les valeurs principales des deux pays
  const cedantCountry = COUNTRIES.find(c => c.id === cedant);
  const acquereurCountry = COUNTRIES.find(c => c.id === acquereur);
  const cedantPrimary = cedantCountry.methods.find(m => m.primary);
  const acquereurPrimary = acquereurCountry.methods.find(m => m.primary);
  const vCedant = manualValues[cedant]?.[cedantPrimary.key] ?? cedantPrimary.getValue(modelResults);
  const vAcquereur = manualValues[acquereur]?.[acquereurPrimary.key] ?? acquereurPrimary.getValue(modelResults);

  const gap = vCedant != null && vAcquereur != null ? vAcquereur - vCedant : null;
  const gapPct = gap != null && vCedant > 0 ? (gap / vCedant) * 100 : null;

  const hasAnyResult = Object.keys(modelResults).length > 0;

  return (
    <div style={{ padding: "24px 0" }}>

      {/* Header nav */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <button
          onClick={onBack}
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "6px 14px", color: "#94a3b8", fontSize: 11, cursor: "pointer" }}
        >
          ← {lang === "fr" ? "Retour" : "Back"}
        </button>
        <div style={{ textAlign: "right" }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#f1f5f9", margin: 0 }}>
            {lang === "fr" ? "Analyse par juridiction" : "Jurisdiction Analysis"}
          </h2>
          <p style={{ fontSize: 11, color: "#C9A84C", fontWeight: 500, margin: 0 }}>
            {lang === "fr" ? "Cédant vs Acquéreur — Méthodes & Référentiels" : "Seller vs Buyer — Methods & Standards"}
          </p>
        </div>
      </div>

      {/* Alerte si aucun calcul */}
      {!hasAnyResult && (
        <div style={{ background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 12, color: "#C9A84C" }}>
          💡 {lang === "fr"
            ? "Aucun modèle calculé. Vous pouvez saisir les valeurs manuellement via le bouton \"Saisir\" sur chaque colonne, ou retourner au dashboard pour lancer des calculs."
            : "No model calculated. Use the \"Enter\" button to input values manually, or go back to the dashboard to run calculations."}
        </div>
      )}

      {/* Gap banner */}
      {gap != null && (
        <div style={{
          background: Math.abs(gapPct) < 5 ? "rgba(21,128,61,0.08)" : "rgba(180,83,9,0.08)",
          border: `1px solid ${Math.abs(gapPct) < 5 ? "rgba(21,128,61,0.3)" : "rgba(180,83,9,0.3)"}`,
          borderRadius: 10, padding: "12px 18px", marginBottom: 20,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>
              Écart de valorisation entre référentiels ({cedantCountry.label} → {acquereurCountry.label})
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: gap >= 0 ? "#4ade80" : "#f87171" }}>
              {gap >= 0 ? "+" : ""}{fC(gap)}
              <span style={{ fontSize: 13, fontWeight: 600, marginLeft: 8, color: gap >= 0 ? "#22c55e" : "#ef4444" }}>
                ({gap >= 0 ? "+" : ""}{gapPct.toFixed(1)} %)
              </span>
            </div>
          </div>
          <div style={{ fontSize: 11, color: "#475569", maxWidth: 320, lineHeight: 1.6 }}>
            {Math.abs(gapPct) < 5
              ? "✓ Convergence forte entre les deux référentiels — risque de contestation fiscale limité."
              : "⚠ Écart significatif — à documenter dans le dossier de prix de transfert (OCDE Ch. IX)."}
          </div>
        </div>
      )}

      {/* Colonnes cédant / acquéreur */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8, paddingLeft: 4 }}>
            🏷 Pays cédant
          </div>
          <CountryColumn
            countryId={cedant}
            modelResults={modelResults}
            manualValues={manualValues}
            onManualChange={handleManualChange}
            onCountryChange={v => { if (v !== acquereur) setCedant(v); }}
          />
        </div>

        {/* Séparateur */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 36, flexShrink: 0 }}>
          <div style={{ width: 1, height: 40, background: "rgba(255,255,255,0.08)" }} />
          <div style={{ padding: "6px 10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, fontSize: 11, color: "#475569", margin: "8px 0", whiteSpace: "nowrap" }}>vs</div>
          <div style={{ width: 1, flex: 1, background: "rgba(255,255,255,0.08)" }} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8, paddingLeft: 4 }}>
            🎯 Pays acquéreur
          </div>
          <CountryColumn
            countryId={acquereur}
            modelResults={modelResults}
            manualValues={manualValues}
            onManualChange={handleManualChange}
            onCountryChange={v => { if (v !== cedant) setAcquereur(v); }}
          />
        </div>
      </div>

      {/* Footer note TP */}
      <div style={{ marginTop: 20, padding: "10px 14px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, fontSize: 10.5, color: "#475569", lineHeight: 1.7 }}>
        <strong style={{ color: "#64748b" }}>Note prix de transfert.</strong>{" "}
        Les méthodes affichées correspondent aux référentiels fiscaux opposables dans chaque juridiction.
        Un écart &gt; 5 % entre les valorisations cédant/acquéreur est susceptible d'être contesté par l'administration
        du pays à fiscalité plus élevée. Documenter selon OCDE TP Ch. IX (profit potential + options réalistes).
        {" "}<span style={{ color: "#334155" }}>Réf. : DGFiP 2006 · Rev. Ruling 59-60 (IRS) · IDW S1/S5 (IDW) · CSI n°28 · SAV Manual (HMRC).</span>
      </div>
    </div>
  );
}
